'use client';

import React, { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const router = useRouter();
  const pathname = usePathname();
  const { authState, isAuthenticated } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [shouldShowContent, setShouldShowContent] = useState(false);

  // パブリックページ（認証不要）のリスト
  const publicPages = ['/login'];
  const isPublicPage = publicPages.includes(pathname);

  // 認証状態に基づく表示制御
  useEffect(() => {
    console.log('AuthGuard状態:', { 
      authState, 
      isAuthenticated, 
      isPublicPage, 
      pathname,
      isRedirecting,
      shouldShowContent
    });

    // パブリックページの場合は即座に表示
    if (isPublicPage) {
      setShouldShowContent(true);
      return;
    }

    // 認証状態に応じた処理
    switch (authState) {
      case 'loading':
        // 認証状態確認中は表示しない
        setShouldShowContent(false);
        setIsRedirecting(false);
        break;
        
      case 'authenticated':
        // 認証済みの場合は表示
        setShouldShowContent(true);
        setIsRedirecting(false);
        break;
        
      case 'unauthenticated':
        // 未認証の場合はリダイレクト
        if (!isRedirecting) {
          console.log('未認証ユーザーを /login にリダイレクトします');
          setShouldShowContent(false);
          setIsRedirecting(true);
          router.push('/login');
        }
        break;
    }
  }, [authState, isAuthenticated, isPublicPage, pathname, router, isRedirecting, shouldShowContent]);

  // リダイレクト完了後の状態リセット
  useEffect(() => {
    if (isAuthenticated && isRedirecting) {
      setIsRedirecting(false);
    }
  }, [isAuthenticated, isRedirecting]);

  // パブリックページの場合は認証チェックなしで表示
  if (isPublicPage) {
    return <>{children}</>;
  }

  // 認証状態が確定していない、またはリダイレクト中の場合はローディング画面
  if (authState === 'loading' || isRedirecting || !shouldShowContent) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div 
            className="animate-spin h-8 w-8 border-4 rounded-full mx-auto mb-4"
            style={{
              borderColor: 'var(--border)',
              borderTopColor: 'var(--primary)'
            }}
          />
          <p style={{ color: 'var(--foreground)' }} className="text-sm">
            {authState === 'loading' ? '認証状態を確認中...' : 'ログインページに移動中...'}
          </p>
        </div>
      </div>
    );
  }

  // 未認証の場合は何も表示しない（リダイレクト処理中）
  if (authState === 'unauthenticated') {
    return null;
  }

  // 認証済みの場合は子コンポーネントをレンダリング
  return <>{children}</>;
};

export default AuthGuard;